import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiEye, FiDownload, FiInfo, FiLoader, FiExternalLink } from 'react-icons/fi';
import ncbClient from '@/utils/ncbClient';
import Accordion from '@/components/Accordion';
import SectionTitle from '@/components/SectionTitle';
import PromptModal from '@/components/PromptModal';

const SavedProjectItem = ({ project }) => {
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const projectDraft = JSON.parse(project.projectDraft || '{}');
  const assets = projectDraft.assets || {};

  const dropboxBaseUrl = 'https://www.dropbox.com/home';

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <PromptModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        promptText={project.generatedPrompt || 'No prompt was generated for this project.'}
      />
      <Accordion title={project.projectName || 'Untitled Project'}>
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-gray-700">Project Details</h4>
            <p className="text-sm text-gray-600"><strong>Developer:</strong> {project.developerName}</p>
            <p className="text-sm text-gray-600"><strong>Created:</strong> {new Date(project.dateCreated).toLocaleString()}</p>
          </div>

          {Object.keys(assets).length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-700">Dropbox Assets</h4>
              <ul className="text-sm text-gray-600 list-disc list-inside">
                {assets.logoPath && <li>Logo: <a href={`${dropboxBaseUrl}${assets.logoPath}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Open in Dropbox <FiExternalLink className="inline-block" /></a></li>}
                {assets.faviconPath && <li>Favicon: <a href={`${dropboxBaseUrl}${assets.faviconPath}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Open in Dropbox <FiExternalLink className="inline-block" /></a></li>}
                {assets.touchIconPath && <li>Touch Icon: <a href={`${dropboxBaseUrl}${assets.touchIconPath}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Open in Dropbox <FiExternalLink className="inline-block" /></a></li>}
              </ul>
            </div>
          )}

          <div>
            <h4 className="font-semibold text-gray-700">Project Draft</h4>
            <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto max-h-48">
              {JSON.stringify(projectDraft, null, 2)}
            </pre>
          </div>

          <div className="flex space-x-2 pt-2">
            <button
              onClick={() => setIsPromptModalOpen(true)}
              disabled={!project.generatedPrompt}
              className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <FiEye className="inline mr-1" /> View Prompt
            </button>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

function SavedProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const fetchedProjects = await ncbClient.getProjects();
        setProjects(fetchedProjects);
        setError(null);
      } catch (err) {
        setError(err.message);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <SectionTitle title="Saved Projects" subtitle="Review and manage your previously saved projects." />
      <div className="bg-white p-8 rounded-xl shadow-lg">
        {loading && (
          <div className="flex justify-center items-center py-10">
            <FiLoader className="animate-spin h-8 w-8 text-indigo-600" />
            <p className="ml-3 text-gray-600">Loading projects...</p>
          </div>
        )}
        {error && (
          <div className="text-center py-10 text-red-500">
            <FiInfo className="mx-auto h-8 w-8 mb-2" />
            <p>Error loading projects: {error}</p>
          </div>
        )}
        {!loading && !error && projects.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            <FiInfo className="mx-auto h-8 w-8 mb-2" />
            <p>You haven't saved any projects yet.</p>
          </div>
        )}
        {!loading && !error && projects.length > 0 && (
          <div className="space-y-4">
            {projects.map((project) => (
              <SavedProjectItem key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default SavedProjects;